import { useEffect, useState, useCallback } from "react";
import api, { fmtMXN, fmtDate, todayLocal } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import ReconciliationTab from "@/components/ReconciliationTab";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Loader2, X, Landmark, Banknote, ArrowLeftRight, Undo2, TrendingUp, TrendingDown } from "lucide-react";

const MOV_TYPES = { ingreso: ["Ingreso", "#86efac"], pago: ["Pago", "#fca5a5"], deposito: ["Depósito", "#86efac"], retiro: ["Retiro", "#fca5a5"], transferencia: ["Transferencia", "#22d3ee"], reverso: ["Reverso", "#f59e0b"] };

export const Modal = ({ title, onClose, children, wide }) => (
  <div className="fixed inset-0 z-50 p-4 flex items-center justify-center" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={onClose}>
    <div className={`panel w-full ${wide ? "max-w-3xl" : "max-w-lg"} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
      <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
        <h3 className="font-bold text-slate-100 text-sm uppercase tracking-widest">{title}</h3>
        <button onClick={onClose} className="btn-ghost p-1" data-testid="tb-modal-close"><X className="w-4 h-4" /></button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

const AccountPicker = ({ accounts, funds, kind, id, onChange, testid }) => (
  <div className="flex gap-2">
    <select className="input-cyber px-2 py-1.5 text-xs" value={kind} onChange={(e) => onChange(e.target.value, "")} data-testid={`${testid}-kind`}>
      <option value="banco">Banco</option>
      <option value="caja">Caja</option>
    </select>
    <select className="input-cyber px-2 py-1.5 text-xs flex-1" value={id} onChange={(e) => onChange(kind, e.target.value)} data-testid={`${testid}-id`}>
      <option value="">Cuenta…</option>
      {(kind === "banco" ? accounts : funds).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
    </select>
  </div>
);

const MovementModal = ({ accounts, funds, prefill, onClose, onSaved }) => {
  const [f, setF] = useState({ date: todayLocal(), type: prefill?.type || "pago", account_kind: "banco", account_id: "",
    amount: prefill?.amount || "", method: "transferencia", bank_reference: "", notes: "", links: prefill?.links || {} });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.account_id) return toast.error("Selecciona la cuenta");
    if (!(Number(f.amount) > 0)) return toast.error("Monto inválido");
    setBusy(true);
    try {
      await api.post("/fin-movements", { ...f, amount: Number(f.amount) });
      toast.success("Movimiento registrado");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={prefill?.title || "Nuevo movimiento"} onClose={onClose}>
      <div className="space-y-3 text-xs">
        {prefill?.label && <div className="panel p-2 text-slate-300" data-testid="mov-link-label">{prefill.label}</div>}
        <div className="grid grid-cols-2 gap-2">
          <input type="date" className="input-cyber px-2 py-1.5" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="mov-date" />
          <select className="input-cyber px-2 py-1.5" value={f.type} disabled={!!prefill?.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="mov-type">
            {["ingreso", "pago", "deposito", "retiro"].map((t) => <option key={t} value={t}>{MOV_TYPES[t][0]}</option>)}
          </select>
        </div>
        <AccountPicker accounts={accounts} funds={funds} kind={f.account_kind} id={f.account_id}
          onChange={(k, i) => setF({ ...f, account_kind: k, account_id: i })} testid="mov-account" />
        <div className="grid grid-cols-2 gap-2">
          <input type="number" min="0" step="any" placeholder="Monto $ *" className="input-cyber px-2 py-1.5" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="mov-amount" />
          <select className="input-cyber px-2 py-1.5" value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })} data-testid="mov-method">
            {["transferencia", "efectivo", "cheque", "tarjeta", "spei"].map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <input className="input-cyber px-2 py-1.5 w-full" placeholder="Referencia bancaria" value={f.bank_reference} onChange={(e) => setF({ ...f, bank_reference: e.target.value })} data-testid="mov-reference" />
        <input className="input-cyber px-2 py-1.5 w-full" placeholder="Notas" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="mov-notes" />
        <div className="flex justify-end">
          <button disabled={busy} onClick={save} className="btn-primary px-4 py-2 text-xs uppercase tracking-widest" data-testid="mov-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Registrar"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const TransferModal = ({ accounts, funds, onClose, onSaved }) => {
  const [f, setF] = useState({ date: todayLocal(), from_kind: "banco", from_id: "", to_kind: "banco", to_id: "", amount: "", bank_reference: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.from_id || !f.to_id) return toast.error("Selecciona origen y destino");
    setBusy(true);
    try {
      await api.post("/fin-movements/transfer", { ...f, amount: Number(f.amount) });
      toast.success("Transferencia registrada (no cuenta como ingreso ni gasto)");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Transferencia entre cuentas propias" onClose={onClose}>
      <div className="space-y-3 text-xs">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Origen</div>
        <AccountPicker accounts={accounts} funds={funds} kind={f.from_kind} id={f.from_id} onChange={(k, i) => setF({ ...f, from_kind: k, from_id: i })} testid="tr-from" />
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Destino</div>
        <AccountPicker accounts={accounts} funds={funds} kind={f.to_kind} id={f.to_id} onChange={(k, i) => setF({ ...f, to_kind: k, to_id: i })} testid="tr-to" />
        <div className="grid grid-cols-2 gap-2">
          <input type="date" className="input-cyber px-2 py-1.5" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="tr-date" />
          <input type="number" min="0" step="any" placeholder="Monto $" className="input-cyber px-2 py-1.5" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="tr-amount" />
        </div>
        <input className="input-cyber px-2 py-1.5 w-full" placeholder="Referencia" value={f.bank_reference} onChange={(e) => setF({ ...f, bank_reference: e.target.value })} data-testid="tr-reference" />
        <div className="flex justify-end">
          <button disabled={busy} onClick={save} className="btn-primary px-4 py-2 text-xs uppercase tracking-widest" data-testid="tr-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : <><ArrowLeftRight className="w-3.5 h-3.5 inline mr-1" /> Transferir</>}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const AccountsTab = ({ accounts, funds, canManage, canSeeBalances, reload }) => {
  const [openAcc, setOpenAcc] = useState(false);
  const [openFund, setOpenFund] = useState(false);
  const [fa, setFa] = useState({ name: "", bank: "", account_number: "", clabe: "", opening_balance: "" });
  const [ff, setFf] = useState({ name: "", kind: "general", project_id: "", opening_balance: "" });
  const [projects, setProjects] = useState([]);
  useEffect(() => { if (openFund) api.get("/projects").then(({ data }) => setProjects(data)); }, [openFund]);
  const saveAcc = async () => {
    if (!fa.name) return toast.error("Nombre requerido");
    try { await api.post("/bank-accounts", { ...fa, opening_balance: Number(fa.opening_balance) || 0 }); toast.success("Cuenta creada"); setOpenAcc(false); setFa({ name: "", bank: "", account_number: "", clabe: "", opening_balance: "" }); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const saveFund = async () => {
    if (!ff.name) return toast.error("Nombre requerido");
    try { await api.post("/cash-funds", { ...ff, project_id: ff.project_id || null, opening_balance: Number(ff.opening_balance) || 0 }); toast.success("Caja creada"); setOpenFund(false); setFf({ name: "", kind: "general", project_id: "", opening_balance: "" }); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const Row = ({ a, icon: Icon }) => (
    <div className="panel px-4 py-3 flex items-center gap-3" data-testid={`account-${a.id}`}>
      <Icon className="w-4 h-4" style={{ color: "var(--cyan)" }} />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-slate-200">{a.name}{a.active === false && <span className="ml-2 text-[9px] text-slate-500">INACTIVA</span>}</div>
        <div className="text-[10px] text-slate-500 font-mono">{a.bank || a.project_name || (a.kind === "general" ? "Caja general" : "")} {a.account_number ? `· ${a.account_number}` : ""}</div>
      </div>
      {canSeeBalances && a.balance != null && <div className="font-mono text-sm font-bold" style={{ color: a.balance >= 0 ? "#86efac" : "#fca5a5" }}>{fmtMXN(a.balance)}</div>}
    </div>
  );
  return (
    <div className="space-y-4" data-testid="accounts-tab">
      <div className="flex items-center gap-2">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 flex-1">// Cuentas bancarias</div>
        {canManage && <button onClick={() => setOpenAcc(true)} className="btn-primary px-3 py-1.5 text-[10px] uppercase tracking-widest" data-testid="new-account-btn"><Plus className="w-3 h-3 inline" /> Cuenta</button>}
      </div>
      {accounts.map((a) => <Row key={a.id} a={a} icon={Landmark} />)}
      {accounts.length === 0 && <div className="panel p-6 text-center text-slate-500 text-sm">Sin cuentas bancarias registradas.</div>}
      <div className="flex items-center gap-2">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 flex-1">// Cajas de efectivo</div>
        {canManage && <button onClick={() => setOpenFund(true)} className="btn-primary px-3 py-1.5 text-[10px] uppercase tracking-widest" data-testid="new-fund-btn"><Plus className="w-3 h-3 inline" /> Caja</button>}
      </div>
      {funds.map((a) => <Row key={a.id} a={a} icon={Banknote} />)}
      {funds.length === 0 && <div className="panel p-6 text-center text-slate-500 text-sm">Sin cajas. Los fondos por comprobar de residentes siguen en Caja / Efectivo.</div>}
      {openAcc && (
        <Modal title="Nueva cuenta bancaria" onClose={() => setOpenAcc(false)}>
          <div className="space-y-2 text-xs">
            <input className="input-cyber px-2 py-1.5 w-full" placeholder="Nombre (ej. BBVA Operadora) *" value={fa.name} onChange={(e) => setFa({ ...fa, name: e.target.value })} data-testid="acc-name" />
            <div className="grid grid-cols-2 gap-2">
              <input className="input-cyber px-2 py-1.5" placeholder="Banco" value={fa.bank} onChange={(e) => setFa({ ...fa, bank: e.target.value })} data-testid="acc-bank" />
              <input className="input-cyber px-2 py-1.5" placeholder="No. de cuenta" value={fa.account_number} onChange={(e) => setFa({ ...fa, account_number: e.target.value })} data-testid="acc-number" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input className="input-cyber px-2 py-1.5" placeholder="CLABE" value={fa.clabe} onChange={(e) => setFa({ ...fa, clabe: e.target.value })} data-testid="acc-clabe" />
              <input type="number" step="any" className="input-cyber px-2 py-1.5" placeholder="Saldo inicial $" value={fa.opening_balance} onChange={(e) => setFa({ ...fa, opening_balance: e.target.value })} data-testid="acc-opening" />
            </div>
            <div className="flex justify-end"><button onClick={saveAcc} className="btn-primary px-4 py-2 text-xs uppercase tracking-widest" data-testid="acc-save">Guardar</button></div>
          </div>
        </Modal>
      )}
      {openFund && (
        <Modal title="Nueva caja de efectivo" onClose={() => setOpenFund(false)}>
          <div className="space-y-2 text-xs">
            <input className="input-cyber px-2 py-1.5 w-full" placeholder="Nombre *" value={ff.name} onChange={(e) => setFf({ ...ff, name: e.target.value })} data-testid="fund-name" />
            <div className="grid grid-cols-2 gap-2">
              <select className="input-cyber px-2 py-1.5" value={ff.kind} onChange={(e) => setFf({ ...ff, kind: e.target.value })} data-testid="fund-kind">
                <option value="general">Caja general</option>
                <option value="obra">Caja por obra</option>
              </select>
              <input type="number" step="any" className="input-cyber px-2 py-1.5" placeholder="Saldo inicial $" value={ff.opening_balance} onChange={(e) => setFf({ ...ff, opening_balance: e.target.value })} data-testid="fund-opening" />
            </div>
            {ff.kind === "obra" && (
              <select className="input-cyber px-2 py-1.5 w-full" value={ff.project_id} onChange={(e) => setFf({ ...ff, project_id: e.target.value })} data-testid="fund-project">
                <option value="">Obra…</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )}
            <div className="flex justify-end"><button onClick={saveFund} className="btn-primary px-4 py-2 text-xs uppercase tracking-widest" data-testid="fund-save">Guardar</button></div>
          </div>
        </Modal>
      )}
    </div>
  );
};

const MovementsTab = ({ accounts, funds, isFull, reloadAll }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMov, setOpenMov] = useState(false);
  const [openTr, setOpenTr] = useState(false);
  const load = useCallback(() => {
    api.get("/fin-movements").then(({ data }) => setRows(data)).catch(() => toast.error("Error al cargar movimientos")).finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);
  const reverse = async (m) => {
    const reason = window.prompt(`Revertir ${m.folio} (${fmtMXN(m.amount)}). El movimiento NO se elimina: se crea un contra-movimiento auditado.\nMotivo del reverso:`);
    if (!reason || reason.trim().length < 3) return;
    try { await api.post(`/fin-movements/${m.id}/reverse`, { reason }); toast.success("Movimiento revertido"); load(); reloadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  if (loading) return <div className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-500" /></div>;
  return (
    <div className="space-y-2" data-testid="movements-tab">
      <div className="flex gap-2">
        <button onClick={() => setOpenMov(true)} className="btn-primary px-3 py-1.5 text-[10px] uppercase tracking-widest" data-testid="new-movement-btn"><Plus className="w-3 h-3 inline" /> Movimiento</button>
        <button onClick={() => setOpenTr(true)} className="btn-ghost px-3 py-1.5 text-[10px] uppercase tracking-widest" style={{ color: "var(--cyan)", border: "1px solid var(--cyan)" }} data-testid="new-transfer-btn"><ArrowLeftRight className="w-3 h-3 inline" /> Transferencia</button>
      </div>
      {rows.length === 0 && <div className="panel p-6 text-center text-slate-500 text-sm">Sin movimientos registrados.</div>}
      {rows.map((m) => {
        const [tl, tc] = MOV_TYPES[m.type] || ["?", "#94a3b8"];
        return (
          <div key={m.id} className="panel px-4 py-2.5 flex items-center gap-3 flex-wrap" data-testid={`mov-${m.id}`} style={{ opacity: m.status === "revertido" ? 0.45 : 1 }}>
            <span className="font-mono text-[11px] text-slate-400">{m.folio}</span>
            <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase" style={{ background: `${tc}18`, color: tc, border: `1px solid ${tc}55` }}>{tl}</span>
            {m.direction === "in" ? <TrendingUp className="w-3.5 h-3.5" style={{ color: "#86efac" }} /> : <TrendingDown className="w-3.5 h-3.5" style={{ color: "#fca5a5" }} />}
            <div className="text-xs text-slate-400 flex-1 min-w-0 truncate">
              {fmtDate(m.date)} · <span className="text-slate-300">{m.account_label}</span>
              {m.link_label ? ` · ${m.link_label}` : ""}{m.bank_reference ? ` · ref ${m.bank_reference}` : ""}
              {m.status === "revertido" && <span style={{ color: "#f59e0b" }}> · REVERTIDO: {m.reversal_reason}</span>}
              {m.reconciled && <span style={{ color: "#86efac" }}> · ✓ conciliado</span>}
            </div>
            <span className="font-mono text-sm font-bold" style={{ color: m.direction === "in" ? "#86efac" : "#fca5a5" }}>
              {m.direction === "in" ? "+" : "−"}{fmtMXN(m.amount)}
            </span>
            {isFull && m.status === "activo" && m.type !== "reverso" && (
              <button onClick={() => reverse(m)} className="btn-ghost p-1.5" title="Revertir" data-testid={`mov-reverse-${m.id}`} style={{ color: "#f59e0b" }}><Undo2 className="w-3.5 h-3.5" /></button>
            )}
          </div>
        );
      })}
      {openMov && <MovementModal accounts={accounts} funds={funds} onClose={() => setOpenMov(false)} onSaved={() => { setOpenMov(false); load(); reloadAll(); }} />}
      {openTr && <TransferModal accounts={accounts} funds={funds} onClose={() => setOpenTr(false)} onSaved={() => { setOpenTr(false); load(); reloadAll(); }} />}
    </div>
  );
};

const AgingBar = ({ aging }) => (
  <div className="flex gap-2 text-[10px] font-mono flex-wrap">
    {Object.entries(aging).map(([b, v]) => (
      <span key={b} className="px-2 py-0.5 rounded-sm" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid var(--border)", color: b === "90+" && v > 0 ? "#fca5a5" : "var(--text-dim)" }}>{b}d: {fmtMXN(v)}</span>
    ))}
  </div>
);

const PayablesTab = ({ accounts, funds, reloadAll }) => {
  const [data, setData] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const load = useCallback(() => { api.get("/treasury-banks/cxp").then(({ data }) => setData(data)); }, []);
  useEffect(load, [load]);
  if (!data) return <div className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-500" /></div>;
  return (
    <div className="space-y-3" data-testid="cxp-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="font-mono text-lg font-bold" style={{ color: "#f59e0b" }}>{fmtMXN(data.total)}</span>
        <AgingBar aging={data.aging} />
      </div>
      {data.rows.length === 0 && <div className="panel p-6 text-center text-slate-500 text-sm">Sin cuentas por pagar: todos los gastos autorizados están pagados.</div>}
      {data.rows.map((r) => (
        <div key={r.expense_id} className="panel px-4 py-2.5 flex items-center gap-3 flex-wrap" data-testid={`cxp-${r.expense_id}`}>
          <div className="text-xs text-slate-400 flex-1 min-w-0 truncate">
            {fmtDate(r.date)} · <span className="text-slate-300">{r.description || r.category}</span>
            {r.vendor ? ` · ${r.vendor}` : ""}{r.project_name ? ` · ${r.project_name}` : ""}
            {r.paid > 0 && <span style={{ color: "#86efac" }}> · pagado {fmtMXN(r.paid)}</span>}
          </div>
          <span className="text-[9px] font-mono px-1.5 rounded-sm" style={{ border: "1px solid var(--border)", color: r.bucket === "90+" ? "#fca5a5" : "var(--text-dim)" }}>{r.bucket}d</span>
          <span className="font-mono text-sm font-bold" style={{ color: "#f59e0b" }}>{fmtMXN(r.saldo)}</span>
          <button onClick={() => setPayFor(r)} className="btn-primary px-2 py-1 text-[10px] uppercase tracking-widest" data-testid={`cxp-pay-${r.expense_id}`}>Pagar</button>
        </div>
      ))}
      {payFor && (
        <MovementModal accounts={accounts} funds={funds}
          prefill={{ type: "pago", amount: payFor.saldo, links: { expense_id: payFor.expense_id }, title: "Pagar gasto", label: `${payFor.description || payFor.category} · saldo ${fmtMXN(payFor.saldo)} (puedes pagar parcial)` }}
          onClose={() => setPayFor(null)} onSaved={() => { setPayFor(null); load(); reloadAll(); }} />
      )}
    </div>
  );
};

const ReceivablesTab = ({ accounts, funds, reloadAll }) => {
  const [data, setData] = useState(null);
  const [collectFor, setCollectFor] = useState(null);
  const load = useCallback(() => { api.get("/treasury-banks/cxc").then(({ data }) => setData(data)); }, []);
  useEffect(load, [load]);
  if (!data) return <div className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-500" /></div>;
  return (
    <div className="space-y-3" data-testid="cxc-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="font-mono text-lg font-bold" style={{ color: "#22d3ee" }}>{fmtMXN(data.total)}</span>
        <AgingBar aging={data.aging} />
      </div>
      {data.rows.length === 0 && <div className="panel p-6 text-center text-slate-500 text-sm">Sin cuentas por cobrar.</div>}
      {data.rows.map((r) => (
        <div key={r.invoice_id} className="panel px-4 py-2.5 flex items-center gap-3 flex-wrap" data-testid={`cxc-${r.invoice_id}`}>
          <span className="font-mono text-xs font-bold text-slate-200">{r.folio}</span>
          <div className="text-xs text-slate-400 flex-1 min-w-0 truncate">
            {fmtDate(r.date)} · {r.client || "—"} · {r.kind}
            {r.cobrado > 0 && <span style={{ color: "#86efac" }}> · cobrado {fmtMXN(r.cobrado)}</span>}
          </div>
          <span className="text-[9px] font-mono px-1.5 rounded-sm" style={{ border: "1px solid var(--border)", color: r.bucket === "90+" ? "#fca5a5" : "var(--text-dim)" }}>{r.bucket}d</span>
          <span className="font-mono text-sm font-bold" style={{ color: "#22d3ee" }}>{fmtMXN(r.saldo)}</span>
          <button onClick={() => setCollectFor(r)} className="btn-primary px-2 py-1 text-[10px] uppercase tracking-widest" data-testid={`cxc-collect-${r.invoice_id}`}>Registrar cobro</button>
        </div>
      ))}
      {collectFor && (
        <MovementModal accounts={accounts} funds={funds}
          prefill={{ type: "ingreso", amount: collectFor.saldo, links: { client_invoice_id: collectFor.invoice_id }, title: `Cobrar ${collectFor.folio}`, label: `El cobro abona automáticamente a la factura ${collectFor.folio} · saldo ${fmtMXN(collectFor.saldo)}` }}
          onClose={() => setCollectFor(null)} onSaved={() => { setCollectFor(null); load(); reloadAll(); }} />
      )}
    </div>
  );
};

const SummaryTab = ({ summary }) => {
  if (!summary) return <div className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-500" /></div>;
  const Kpi = ({ label, value, color }) => (
    <div className="panel p-4" data-testid={`tb-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div className="font-mono text-lg font-bold" style={{ color }}>{fmtMXN(value)}</div>
    </div>
  );
  return (
    <div className="space-y-4" data-testid="tb-summary-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Disponible total" value={summary.disponible} color="#86efac" />
        <Kpi label="Por cobrar" value={summary.cxc_total} color="#22d3ee" />
        <Kpi label="Por pagar" value={summary.cxp_total} color="#f59e0b" />
        <Kpi label="Posición neta" value={summary.posicion_neta} color={summary.posicion_neta >= 0 ? "#86efac" : "#fca5a5"} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="panel p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">// Saldos por cuenta</div>
          {[...summary.bancos, ...summary.cajas].filter((a) => a.active !== false).map((a) => (
            <div key={a.id} className="flex justify-between py-1 text-xs border-b last:border-0" style={{ borderColor: "var(--border)" }}>
              <span className="text-slate-300">{a.name}</span>
              <span className="font-mono" style={{ color: a.balance >= 0 ? "#86efac" : "#fca5a5" }}>{fmtMXN(a.balance)}</span>
            </div>
          ))}
          <div className="flex justify-between py-1 text-xs mt-1">
            <span className="text-slate-500">Efectivo por comprobar (campo)</span>
            <span className="font-mono" style={{ color: "#f59e0b" }}>{fmtMXN(summary.efectivo_por_comprobar)}</span>
          </div>
        </div>
        <div className="panel p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">// Flujo proyectado (8 semanas)</div>
          {summary.flujo_proyectado.map((w) => (
            <div key={w.week} className="flex justify-between py-0.5 text-xs">
              <span className="text-slate-400 font-mono">{w.week}</span>
              <span className="font-mono" style={{ color: w.projected >= 0 ? "#86efac" : "#fca5a5" }}>{fmtMXN(w.projected)}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="panel p-4">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">// Próximos pagos / vencimientos</div>
        {summary.proximos_pagos.length === 0 && <div className="text-xs text-slate-500">Sin compromisos pendientes.</div>}
        {summary.proximos_pagos.map((p) => (
          <div key={p.expense_id} className="flex justify-between py-1 text-xs border-b last:border-0" style={{ borderColor: "var(--border)" }}>
            <span className="text-slate-300 truncate">{fmtDate(p.date)} · {p.description || p.category}{p.vendor ? ` · ${p.vendor}` : ""}</span>
            <span className="font-mono" style={{ color: "#f59e0b" }}>{fmtMXN(p.saldo)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function TreasuryBanks() {
  const { user } = useAuth();
  const isFull = ["admin", "contador"].includes(user?.role);
  const [tab, setTab] = useState(isFull ? "resumen" : "movimientos");
  const [accounts, setAccounts] = useState([]);
  const [funds, setFunds] = useState([]);
  const [summary, setSummary] = useState(null);
  const reload = useCallback(() => {
    api.get("/bank-accounts").then(({ data }) => setAccounts(data));
    api.get("/cash-funds").then(({ data }) => setFunds(data));
    if (isFull) api.get("/treasury-banks/summary").then(({ data }) => setSummary(data));
  }, [isFull]);
  useEffect(reload, [reload]);

  const accWithBal = summary ? summary.bancos : accounts;
  const fundsWithBal = summary ? summary.cajas : funds;
  const TABS = [
    ...(isFull ? [["resumen", "Resumen"]] : []),
    ["cuentas", "Cuentas y cajas"],
    ["movimientos", "Movimientos"],
    ...(isFull ? [["cxp", "Por pagar"], ["cxc", "Por cobrar"], ["conciliacion", "Conciliación"]] : []),
  ];
  return (
    <div data-testid="treasury-banks-page">
      <PageHeader title="Tesorería y Bancos" subtitle="Finanzas" testId="treasury-banks-header" />
      <div className="px-6 lg:px-8 py-6 space-y-4">
        <div className="flex gap-1 flex-wrap">
          {TABS.map(([v, l]) => (
            <button key={v} data-testid={`tb-tab-${v}`} onClick={() => { setTab(v); if (v === "resumen") reload(); }}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-widest rounded-sm border ${tab === v ? "text-cyan-300" : "text-slate-500 hover:text-slate-300"}`}
              style={{ borderColor: tab === v ? "var(--cyan)" : "var(--border)", background: tab === v ? "rgba(34,211,238,0.07)" : "transparent" }}>{l}</button>
          ))}
        </div>
        {tab === "resumen" && isFull && <SummaryTab summary={summary} />}
        {tab === "cuentas" && <AccountsTab accounts={accWithBal} funds={fundsWithBal} canManage={isFull} canSeeBalances={isFull} reload={reload} />}
        {tab === "movimientos" && <MovementsTab accounts={accounts} funds={funds} isFull={isFull} reloadAll={reload} />}
        {tab === "cxp" && isFull && <PayablesTab accounts={accounts} funds={funds} reloadAll={reload} />}
        {tab === "cxc" && isFull && <ReceivablesTab accounts={accounts} funds={funds} reloadAll={reload} />}
        {tab === "conciliacion" && isFull && <ReconciliationTab accounts={accounts} />}
      </div>
    </div>
  );
}
